import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, MapPin, Clock, Users, Check, ArrowLeft } from 'lucide-react';

const UIDesigner = () => {
  const jobDetails = {
    title: "UI Designer",
    department: "Design",
    location: "Perth, Australia",
    type: "Full-time",
    experience: "2-4 years",
    description: "Join our design team to create beautiful, intuitive user interfaces that make technology accessible to everyone. You'll work on exciting projects from web applications to mobile apps, including our flagship product GoFella.",
    responsibilities: [
      "Design user interfaces for web applications and mobile apps",
      "Create and maintain design systems and component libraries", 
      "Collaborate with developers to ensure pixel-perfect implementation",
      "Conduct user research and usability testing",
      "Iterate on designs based on user feedback and data insights",
      "Work closely with product managers and stakeholders"
    ],
    requirements: [
      "2-4 years of UI design experience",
      "Proficiency in Figma, Sketch, or Adobe XD", 
      "Strong portfolio demonstrating design thinking",
      "Understanding of responsive design principles",
      "Knowledge of design systems and component libraries",
      "Excellent communication and collaboration skills"
    ],
    whatWeOffer: [
      "Supportive team environment with experienced designers",
      "Opportunity to work on innovative products like GoFella",
      "Flexible working arrangements and work-life balance",
      "Professional development and growth opportunities",
      "Chance to make a real impact on Australian users"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Back Navigation */}
      <section className="py-6 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/careers" className="inline-flex items-center text-vincotek-blue hover:text-blue-700 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Careers
          </Link>
        </div>
      </section>

      {/* Job Header */}
      <section className="py-12 bg-gradient-to-br from-vincotek-blue to-blue-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {jobDetails.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Badge className="bg-white text-vincotek-blue px-3 py-1">
                {jobDetails.department}
              </Badge>
              <div className="flex items-center text-blue-100">
                <MapPin className="h-4 w-4 mr-2" />
                {jobDetails.location}
              </div>
              <div className="flex items-center text-blue-100">
                <Clock className="h-4 w-4 mr-2" />
                {jobDetails.type}
              </div>
              <div className="flex items-center text-blue-100">
                <Users className="h-4 w-4 mr-2" />
                {jobDetails.experience}
              </div>
            </div>

            <p className="text-xl text-blue-100 leading-relaxed">
              {jobDetails.description}
            </p>
          </div>
        </div>
      </section>

      {/* About the Role */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8">
              About the Role
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              As a UI Designer at Vincotek, you'll be at the forefront of creating user experiences that matter. 
              You'll work on products that real Australians use daily, from our innovative GoFella platform to 
              custom solutions for our clients.
            </p>
            
            <h3 className="text-2xl font-bold text-vincotek-dark mb-6">Key Responsibilities</h3>
            <div className="space-y-4">
              {jobDetails.responsibilities.map((responsibility, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-vincotek-blue flex-shrink-0 mt-1" />
                  <p className="text-gray-700 leading-relaxed">
                    {responsibility}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Experience */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8">
              Skills & Experience
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {jobDetails.requirements.map((requirement, index) => (
                <Card key={index} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-vincotek-yellow rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-vincotek-dark font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {requirement}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8">
              What We Offer
            </h2>
            
            <div className="space-y-4">
              {jobDetails.whatWeOffer.map((offer, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {offer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              How to Apply
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Ready to join our design team and help shape the future of digital products in Australia? 
              Submit your application through our careers page with your portfolio and cover letter.
            </p>
            <Link to="/careers#application">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Apply for This Position
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UIDesigner;